import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext(null);

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(() => {
    const saved = localStorage.getItem('cart');
    return saved ? JSON.parse(saved) : {};
  });

  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cart));
  }, [cart]);

  const addToCart = (storeIdOrProduct, productOrQuantity = 1, selectedColor = null, selectedSize = null) => {
    // Support both calling conventions:
    // 1. addToCart(product, quantity) - original
    // 2. addToCart(storeId, product, color, size) - used by public pages with variations
    let product, quantity, storeId, color, size;
    
    if (typeof storeIdOrProduct === 'string') {
      // Called as addToCart(storeId, product, color, size)
      storeId = storeIdOrProduct;
      product = productOrQuantity;
      quantity = 1;
      color = selectedColor;
      size = selectedSize;
    } else {
      // Called as addToCart(product, quantity)
      product = storeIdOrProduct;
      quantity = typeof productOrQuantity === 'number' ? productOrQuantity : 1;
      storeId = product.store_id;
      color = null;
      size = null;
    }
    
    if (!storeId || !product || !product.id) {
      console.error('Invalid addToCart call:', { storeId, product });
      return;
    }
    
    setCart(prev => {
      const storeCart = prev[storeId] || { items: [], storeName: product.store_name };
      
      // Para produtos com variações, criar uma chave única que inclui cor e tamanho
      const itemKey = color || size 
        ? `${product.id}-${color || ''}-${size || ''}`
        : product.id;
      
      const existingIndex = storeCart.items.findIndex(item => {
        const existingKey = item.selectedColor || item.selectedSize
          ? `${item.product.id}-${item.selectedColor || ''}-${item.selectedSize || ''}`
          : item.product.id;
        return existingKey === itemKey;
      });
      
      if (existingIndex >= 0) {
        const newItems = [...storeCart.items];
        newItems[existingIndex].quantity += quantity;
        return { ...prev, [storeId]: { ...storeCart, items: newItems } };
      } else {
        return {
          ...prev,
          [storeId]: {
            ...storeCart,
            items: [...storeCart.items, { 
              product, 
              quantity,
              selectedColor: color,
              selectedSize: size
            }]
          }
        };
      }
    });
  };

  const removeFromCart = (storeId, productId, selectedColor = null, selectedSize = null) => {
    setCart(prev => {
      const storeCart = prev[storeId];
      if (!storeCart) return prev;
      
      const newItems = storeCart.items.filter(item => {
        // Se tem variações, precisa comparar tudo
        if (selectedColor || selectedSize) {
          return !(item.product.id === productId && 
                   item.selectedColor === selectedColor && 
                   item.selectedSize === selectedSize);
        }
        return item.product.id !== productId;
      });
      
      if (newItems.length === 0) {
        const newCart = { ...prev };
        delete newCart[storeId];
        return newCart;
      }
      
      return { ...prev, [storeId]: { ...storeCart, items: newItems } };
    });
  };

  const updateQuantity = (storeId, productId, quantity, selectedColor = null, selectedSize = null) => {
    if (quantity <= 0) {
      removeFromCart(storeId, productId, selectedColor, selectedSize);
      return;
    }
    
    setCart(prev => {
      const storeCart = prev[storeId];
      if (!storeCart) return prev;
      
      const newItems = storeCart.items.map(item => {
        // Se tem variações, precisa comparar tudo
        if (selectedColor || selectedSize) {
          if (item.product.id === productId && 
              item.selectedColor === selectedColor && 
              item.selectedSize === selectedSize) {
            return { ...item, quantity };
          }
        } else if (item.product.id === productId) {
          return { ...item, quantity };
        }
        return item;
      });
      
      return { ...prev, [storeId]: { ...storeCart, items: newItems } };
    });
  };

  const clearStoreCart = (storeId) => {
    setCart(prev => {
      const newCart = { ...prev };
      delete newCart[storeId];
      return newCart;
    });
  };

  const clearCart = () => {
    setCart({});
  };

  const getCartTotal = (storeId) => {
    const storeCart = cart[storeId];
    if (!storeCart) return 0;
    return storeCart.items.reduce((total, item) => total + (item.product.price * item.quantity), 0);
  };

  const getTotalItems = () => {
    return Object.values(cart).reduce((total, storeCart) =>
      total + storeCart.items.reduce((sum, item) => sum + item.quantity, 0), 0
    );
  };

  return (
    <CartContext.Provider value={{
      cart,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearStoreCart,
      clearCart,
      getCartTotal,
      getTotalItems
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};
